import { useLang } from '../_metronic/i18n/Metronici18n'
import { toPersianDigits } from './formatNumber'

/**
 * Wraps a mixed date+time string in a Unicode LTR isolate (U+2066 … U+2069) so
 * that, in an RTL (Persian) context, the bidi algorithm does not reorder the
 * two numeric groups around the ` - ` separator — i.e. `1405/04/28 - 10:21`
 * must stay date-then-time, never flip to `10:21 - 1405/04/28`.
 * The isolate characters are zero-width and invisible.
 */
const LRI = String.fromCharCode(0x2066) // Left-to-Right Isolate
const PDI = String.fromCharCode(0x2069) // Pop Directional Isolate
const asLtr = (s: string): string => `${LRI}${s}${PDI}`

export function gregorianToJalali(gy: number, gm: number, gd: number): [number, number, number] {
  const g_d_no = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334]
  let jy = gy - 1600
  let gm0 = gm - 1
  let gd0 = gd - 1
  let g_day_no = 365 * jy + Math.floor((jy + 3) / 4) - Math.floor((jy + 99) / 100) + Math.floor((jy + 399) / 400)
  g_day_no += g_d_no[gm0] + gd0
  if (gm0 > 1 && ((gy % 4 === 0 && gy % 100 !== 0) || gy % 400 === 0)) g_day_no++
  const j_day_no = g_day_no - 79
  const j_np = Math.floor(j_day_no / 12053)
  const j_day_no2 = j_day_no % 12053
  let jy2 = 979 + 33 * j_np + 4 * Math.floor(j_day_no2 / 1461)
  let j_day_no3 = j_day_no2 % 1461
  if (j_day_no3 >= 366) { jy2 += Math.floor((j_day_no3 - 1) / 365); j_day_no3 = (j_day_no3 - 1) % 365 }
  const j_mi = [31, 31, 31, 31, 31, 31, 30, 30, 30, 30, 30, 29]
  let jm = 0
  for (let i = 0; i < 11 && j_day_no3 >= j_mi[i]; i++) { j_day_no3 -= j_mi[i]; jm++ }
  return [jy2, jm + 1, j_day_no3 + 1]
}

/**
 * Formats a date/timestamp as Gregorian `yyyy/mm/dd` or `yyyy/mm/dd - hh:mm`.
 * Returns the original input untouched if it can't be parsed as a date
 * (e.g. ApexCharts category placeholders like "start").
 */
export function formatGregorianDate(dateInput: string | number | Date, withTime = true): string {
  const d = new Date(dateInput)
  if (isNaN(d.getTime())) return String(dateInput)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  if (!withTime) return `${year}/${month}/${day}`
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  return asLtr(`${year}/${month}/${day} - ${hours}:${minutes}`)
}

/**
 * Formats a date/timestamp as Jalali (Shamsi) `yyyy/mm/dd` or `yyyy/mm/dd - hh:mm`.
 * Returns the original input untouched if it can't be parsed as a date.
 */
export function formatJalaliDate(dateInput: string | number | Date, withTime = true): string {
  const d = new Date(dateInput)
  if (isNaN(d.getTime())) return String(dateInput)
  const [jy, jm, jd] = gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate())
  const datePart = `${jy}/${String(jm).padStart(2, '0')}/${String(jd).padStart(2, '0')}`
  if (!withTime) return datePart
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  return asLtr(`${datePart} - ${hours}:${minutes}`)
}

/**
 * Locale-aware date formatter: Jalali for `fa`, Gregorian otherwise.
 * Use this anywhere a date is rendered outside of `<ConvertDate>` (e.g. chart formatters).
 */
export function formatLocaleDate(dateInput: string | number | Date, lang: string | undefined, withTime = true): string {
  return lang === 'fa' ? formatJalaliDate(dateInput, withTime) : formatGregorianDate(dateInput, withTime)
}

/** Jalali (Persian) month names, indexed 0 (Farvardin) … 11 (Esfand). */
export const PERSIAN_MONTHS = [
  'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
] as const

const EN_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

/**
 * The three date shapes the dashboards and the asset-insights pages use.
 *
 * These are NAMED rather than pattern-driven on purpose. The reference
 * implementation takes a dayjs format string backed by the `jalaliday` plugin;
 * this project already has its own Jalali conversion (`gregorianToJalali`
 * above) and adding a second one — plus a dependency — to serve three call
 * shapes would be two implementations of the same calendar.
 *
 * Persian dates are always DERIVED from the Gregorian value. Backend Jalali
 * strings are ignored.
 */
type DateParts = { year: number; monthIndex: number; day: number; hh: string; mm: string }

// An unparseable value yields `null` here, and the three formatters below turn
// that into an empty string — NOT the raw input that `formatGregorianDate` and
// `formatJalaliDate` echo back above. The two conventions are deliberate: the
// older pair feeds chart axes, where echoing a placeholder is debuggable, while
// these three feed dashboard figures, where a stray raw string in a stat tile
// reads as data. Empty also matches the reference implementation these pages
// are migrating from.
function parts(dateInput: string | number | Date, lang: string | undefined): DateParts | null {
  const d = new Date(dateInput)
  if (isNaN(d.getTime())) return null
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  if (lang === 'fa') {
    const [jy, jm, jd] = gregorianToJalali(d.getFullYear(), d.getMonth() + 1, d.getDate())
    return { year: jy, monthIndex: jm - 1, day: jd, hh, mm }
  }
  return { year: d.getFullYear(), monthIndex: d.getMonth(), day: d.getDate(), hh, mm }
}

// Digit conversion is NOT reimplemented here — `toPersianDigits` already exists
// in `./formatNumber`, and a second copy would be a second thing to get wrong.
const localize = (s: string, lang: string | undefined): string =>
  lang === 'fa' ? toPersianDigits(s) : s

/** The month name for a set of parts, in the reader's calendar. */
function monthName(p: DateParts, lang: string | undefined): string {
  return lang === 'fa' ? PERSIAN_MONTHS[p.monthIndex] : EN_MONTHS[p.monthIndex]
}

/**
 * `7 Sep 2026` / `۱۶ شهریور ۱۴۰۵`, built from parts that have already been
 * derived. Shared so `formatStamp` composes the same label `formatDay` returns
 * without parsing the date a second time to get it.
 */
function dayLabel(p: DateParts, lang: string | undefined): string {
  return `${localize(String(p.day), lang)} ${monthName(p, lang)} ${localize(String(p.year), lang)}`
}

/** A week or short-day label: `7 Sep` / `۱۶ شهریور`. */
export function formatWeek(dateInput: string | number | Date, lang: string | undefined): string {
  const p = parts(dateInput, lang)
  if (!p) return ''
  return `${localize(String(p.day), lang)} ${monthName(p, lang)}`
}

/** A full day label: `7 Sep 2026` / `۱۶ شهریور ۱۴۰۵`. */
export function formatDay(dateInput: string | number | Date, lang: string | undefined): string {
  const p = parts(dateInput, lang)
  if (!p) return ''
  return dayLabel(p, lang)
}

/** A timestamp: `7 Sep 2026, 08:30` / `۱۶ شهریور ۱۴۰۵، ۰۸:۳۰`. */
export function formatStamp(dateInput: string | number | Date, lang: string | undefined): string {
  const p = parts(dateInput, lang)
  if (!p) return ''
  const time = localize(`${p.hh}:${p.mm}`, lang)
  // The whole string is isolated so bidi does not flip date and time around the
  // separator in an RTL context — the same reason `formatGregorianDate` does it.
  return lang === 'fa' ? asLtr(`${dayLabel(p, lang)}، ${time}`) : `${dayLabel(p, lang)}, ${time}`
}

/**
 * A month label for the lane chart's month rules: `Aug 2026` / `مرداد ۱۴۰۵`.
 *
 * Reads UTC components — the ONE formatter in this file that does not go
 * through `parts()`. The lane chart's month boundaries
 * (`asset_insights/laneChart.ts`'s `buildLaneChart`) are computed on the UTC
 * clock (`setUTCDate(1)`, `setUTCHours(0,…)`), matching the reference exactly.
 * A `MonthRule.at` timestamp is only ever fed back through this formatter, so
 * the two must share one clock: reading LOCAL components here instead would
 * mislabel a rule landing on the UTC 1st as the previous month in any
 * negative-UTC-offset timezone. Every other formatter in this file
 * (`formatDay`/`formatWeek`/`formatStamp`) intentionally reads local
 * components, because those label a moment in the reader's own day — this one
 * labels an axis boundary that was deliberately drawn in UTC.
 */
export function formatMonth(dateInput: string | number | Date, lang: string | undefined): string {
  const d = new Date(dateInput)
  if (isNaN(d.getTime())) return ''
  if (lang === 'fa') {
    const [jy, jm] = gregorianToJalali(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate())
    return `${PERSIAN_MONTHS[jm - 1]} ${localize(String(jy), lang)}`
  }
  return `${EN_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

export default function ConvertDate({ date, className }: { date: string, className?: string }) {
  const lang = useLang()?.lang.selectedLang

  // Persian dates are always derived from the Gregorian date, never taken from
  // the backend's Jalali fields (project-wide rule).
  const displayDate = lang === 'fa' ? formatJalaliDate(date) : formatGregorianDate(date)

  return (
    <div className={`Date ${className}`}>
      <span>{displayDate}</span>
    </div>
  )
}

